import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, ArrowRight, Briefcase } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useSmartCTA } from "@/hooks/useSmartCTA";
import api, { mediaUrl } from "@/lib/api";
import { categoryLabel } from "@/lib/catalog";
import { budgetText, jobLocationText } from "@/lib/jobDisplay";
import { formatDate } from "@/lib/dates";

const PAGE_SIZE = 12;

export default function PublicJobs() {
  const { t } = useTranslation();
  const cta = useSmartCTA();
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (nextSkip, replace) => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("limit", String(PAGE_SIZE));
    params.set("skip", String(nextSkip));
    if (category) params.set("category_id", category);
    try {
      const { data } = await api.get(`/jobs/public?${params}`);
      setTotal(data.total || 0);
      setJobs((prev) => (replace ? data.jobs || [] : [...prev, ...(data.jobs || [])]));
      setSkip(nextSkip);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    api.get("/catalog/categories")
      .then((r) => setCategories(r.data.categories || []))
      .catch((e) => console.warn("[PublicJobs] categories failed to load", e));
  }, []);
  useEffect(() => {
    load(0, true);
  }, [load]);

  const hasMore = jobs.length < total;

  return (
    <div className="min-h-screen flex flex-col bg-cream" data-testid="public-jobs-page">
      <Navbar />
      <main className="flex-1">
        <section className="section bg-honeycomb">
          <div className="container-app">
            <span className="badge mb-4"><Briefcase size={12} className="text-hive-ink" /> {t("publicJobs.kicker")}</span>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold text-worker text-balance">{t("publicJobs.title")}</h1>
            {/*
              Same fix as the detail page's card: the subtitle and the CTA were
              a recruitment pitch printed over a live session. The board is
              public and the hero stays a pitch for visitors — for everyone else
              it says the true next step, which for an onboarded FixyBee is
              their own board rather than a page inviting them to become one.
            */}
            {(() => {
              const h = cta.browseOpenJobs();
              if (h.state === "pending") {
                return (
                  <>
                    <div className="skeleton h-6 w-2/3 max-w-2xl mt-3" />
                    <div className="skeleton h-11 w-64 mt-6" />
                  </>
                );
              }
              return (
                <>
                  <p className="mt-3 text-lg text-smoke max-w-2xl">
                    {t(`publicJobs.hero.${h.state}.subtitle`)}
                  </p>
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <Link to={h.to} className="fx-btn fx-btn--primary" data-testid="public-jobs-cta">
                      {t(`publicJobs.hero.${h.state}.cta`)} <ArrowRight size={18} />
                    </Link>
                    <span className="text-sm text-smoke">{t(`publicJobs.hero.${h.state}.ctaSub`)}</span>
                  </div>
                </>
              );
            })()}
          </div>
        </section>

        <section className="section">
          <div className="container-app">
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="input-base max-w-xs"
                data-testid="public-jobs-category"
              >
                <option value="">{t("publicJobs.allCategories")}</option>
                {categories.map((c) => (
                  <option key={c.category_id} value={c.category_id}>{categoryLabel(t, c)}</option>
                ))}
              </select>
              <span className="text-sm text-smoke" data-testid="public-jobs-count">
                {t("publicJobs.count", { count: total })}
              </span>
            </div>

            {jobs.length === 0 && !loading ? (
              <p className="text-smoke" data-testid="public-jobs-empty">{t("publicJobs.empty")}</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="public-jobs-grid">
                {jobs.map((j) => (
                  <Link
                    key={j.job_id}
                    to={`/open-jobs/${j.job_id}`}
                    className="card-base overflow-hidden flex flex-col hover:border-hive transition-colors"
                    data-testid={`public-job-${j.job_id}`}
                  >
                    {j.photos?.[0] && (
                      <img src={mediaUrl(j.photos[0])} alt="" className="w-full h-40 object-cover" />
                    )}
                    <div className="p-4 flex flex-col flex-1">
                      <span className="chip self-start mb-2">{categoryLabel(t, j.category)}</span>
                      <h3 className="font-display text-lg font-bold text-worker line-clamp-2">{j.title}</h3>
                      <p className="mt-1 text-sm text-smoke line-clamp-3">{j.description}</p>
                      <div className="mt-3 pt-3 border-t border-sand text-xs text-smoke space-y-1">
                        {(j.location?.city || j.location?.state) && (
                          <p className="inline-flex items-center gap-1">
                            <MapPin size={12} /> {[j.location.city, j.location.state].filter(Boolean).join(", ")}
                          </p>
                        )}
                        {(j.budget_min > 0 || j.budget_max > 0) && (
                          <p>{budgetText(t, j.budget_min, j.budget_max)}</p>
                        )}
                        <p>{t("publicJobs.postedOn", { date: formatDate(j.created_at) })}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {hasMore && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => load(skip + PAGE_SIZE, false)}
                  disabled={loading}
                  className="fx-btn fx-btn--secondary"
                  data-testid="public-jobs-more"
                >
                  {loading ? t("common.loading") : t("publicJobs.loadMore")}
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
